import Button from "@ui/button";
import Logo from "@ui/logo";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

interface AuthLayoutProps {
  children?: React.ReactNode;
}

export default function AuthLayout({ children }: AuthLayoutProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const homePath = `/${i18n.language}/`;

  return (
    // Cambiamos justify-center por py-12 para que en móviles respire si sale el teclado
    <div className="relative w-full min-h-dvh flex flex-col items-center justify-start sm:justify-center p-4 md:p-8 overflow-x-hidden bg-background transition-colors duration-500 py-16 sm:py-8">
      {/* Fondo decorativo */}
      <div className="absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] bg-size-[32px_32px] opacity-20 pointer-events-none" />

      {/* Botón Volver: En móvil lo hacemos un poco más discreto */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10">
        <Button
          design="ghost"
          size="sm"
          to={homePath}
          icon={ArrowLeft}
          responsiveLabel
          label={t("back_home")}
          className="rounded-full"
        />
      </div>

      <main className="relative w-full max-w-md z-10 flex flex-col">
        {/* LOGO AREA: Reducimos el margen en móvil */}
        <div className="flex flex-col items-center mb-6 md:mb-8 animate-in fade-in zoom-in duration-700">
          <Logo variant="icon" theme="auto" size="xl" className="w-full" />
        </div>

        {/* ANIMATED CONTENT CARD */}
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }} // Cambio scale por y para un slide más natural
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            // Ajustamos el rounded para que sea prolijo pero no invasivo
            className="card-glass rounded-4xl md:rounded-[3.5rem] p-6 md:p-12 shadow-2xl border-border bg-card/50 backdrop-blur-md"
          >
            {children ?? <Outlet />}
          </motion.div>
        </AnimatePresence>

        {/* FOOTER TEXT */}
        <p className="label-nano mt-8 text-center opacity-40">
          Andar360 © {new Date().getFullYear()}
        </p>
      </main>
    </div>
  );
}
